import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { TABLE_FONT_FAMILY } from '../../../theme';
import { formatWeekSpan, isoWeekDates, isoWeekOf } from '../../../utils/isoWeek';

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

/** Below this height the ledger stays as it is. Above it, the number sits at the top and the days sit at the bottom. */
const TALL = '@container (min-height: 160px)';

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const fullDate = (date: Date) =>
  date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const WeekNumberWidget: React.FC = () => {
  const theme = useTheme();
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => {
      setToday((prev) => {
        const next = new Date();
        return prev.toDateString() === next.toDateString() ? prev : next;
      });
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { week, year } = isoWeekOf(today);
  const days = isoWeekDates(today);
  const span = formatWeekSpan(days, year);
  const dark = theme.palette.mode === 'dark';
  const border = dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 33, 52, 0.12)';
  const todayBg = dark ? '#ccba9b' : '#0f2134';
  const todayFg = dark ? '#0f2134' : '#ffffff';
  const ink = dark ? '#ffffff' : '#0f2134';

  return (
    <Box
      role="group"
      aria-label={`ISO week ${week} of ${year}, ${span}. Today is ${fullDate(today)}.`}
      sx={{
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: '6.5rem',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          containerType: 'size',
          '& ::selection': {
            backgroundColor: dark ? 'rgba(204, 186, 155, 0.45)' : 'rgba(15, 33, 52, 0.16)',
            color: dark ? '#0f2134' : '#ffffff',
          },
        }}
      >
      <Box
        aria-hidden
        sx={{
          display: 'none',
          [TALL]: {
            display: 'flex',
            flex: 1,
            minHeight: 0,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 0.5,
          },
        }}
      >
        <Box
          component="p"
          sx={{
            m: 0,
            color: ink,
            fontFamily: TABLE_FONT_FAMILY,
            fontSize: 'clamp(2.5rem, 22cqh, 4.5rem)',
            fontWeight: 500,
            lineHeight: 0.9,
            letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {week}
        </Box>
        <Typography
          sx={{
            m: 0,
            color: 'text.secondary',
            fontFamily: TABLE_FONT_FAMILY,
            fontSize: '0.8rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
            lineHeight: 1.2,
          }}
        >
          {span}
        </Typography>
      </Box>

      <Box
        sx={{
          [TALL]: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: 0,
          },
        }}
      >
      <Box
        sx={{
          display: 'grid',
          width: '100%',
          gridTemplateColumns: '4.25rem repeat(7, minmax(0, 1fr))',
          gridTemplateRows: '1.85rem 2.75rem',
          border: `1px solid ${border}`,
          borderRadius: '4px',
          overflow: 'hidden',
          [TALL]: {
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          },
        }}
      >
            <Box
              sx={{
                gridRow: '1 / span 2',
                display: 'grid',
                gridTemplateRows: '1.85rem 2.75rem',
                borderRight: `1px solid ${border}`,
                backgroundColor: dark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(15, 33, 52, 0.03)',
                [TALL]: { display: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: `1px solid ${border}`,
                  color: 'text.secondary',
                  fontFamily: TABLE_FONT_FAMILY,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {year}
              </Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: TABLE_FONT_FAMILY,
                  fontSize: '2rem',
                  fontWeight: 500,
                  lineHeight: 1,
                  letterSpacing: '-0.03em',
                  fontVariantNumeric: 'tabular-nums',
                  color: ink,
                }}
              >
                {week}
              </Box>
            </Box>

          {days.map((date, index) => {
            const current = isSameDay(date, today);
            const weekend = index >= 5;
            return (
              <Box
                key={`label-${date.toDateString()}`}
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: index < 6 ? `1px solid ${current ? todayBg : border}` : 'none',
                  borderBottom: `1px solid ${current ? todayBg : border}`,
                  backgroundColor: current ? todayBg : 'transparent',
                  color: current ? todayFg : weekend ? 'text.secondary' : ink,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                  textTransform: 'uppercase',
                }}
              >
                {DAY_LABELS[index]}
              </Box>
            );
          })}

          {days.map((date, index) => {
            const current = isSameDay(date, today);
            const weekend = index >= 5;
            return (
              <Box
                key={`date-${date.toDateString()}`}
                aria-current={current ? 'date' : undefined}
                aria-label={current ? `Today, ${fullDate(date)}` : fullDate(date)}
                title={fullDate(date)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: index < 6 ? `1px solid ${current ? todayBg : border}` : 'none',
                  backgroundColor: current ? todayBg : 'transparent',
                  color: current ? todayFg : weekend ? 'text.secondary' : ink,
                  fontFamily: TABLE_FONT_FAMILY,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}
              >
                {date.getDate()}
              </Box>
            );
          })}
        </Box>
      </Box>

        <Typography
          title={span}
          sx={{
            m: 0,
            pt: 0.75,
            pl: '4.25rem',
            boxSizing: 'border-box',
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
            lineHeight: 1.2,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [TALL]: { display: 'none' },
          }}
        >
          {span}
        </Typography>
      </Box>
    </Box>
  );
};

export default WeekNumberWidget;
